import { useState } from 'react'
import { 
  Card, Upload, Button, Form, Input, Select, message, 
  Typography, Space, Steps 
} from 'antd'
import { 
  CloudUploadOutlined, FileOutlined, CheckCircleOutlined 
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

const { Title, Text } = Typography
const { TextArea } = Input
const { Dragger } = Upload
const { Option } = Select

const SchemaUploadPage = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [step, setStep] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [yamlFile, setYamlFile] = useState<File | null>(null)
  const [generatedDatasetId, setGeneratedDatasetId] = useState<string>('')

  const uploadProps = {
    accept: '.yaml,.yml',
    maxCount: 1,
    beforeUpload: (file: File) => {
      setYamlFile(file)
      return false // Prevent auto-upload
    },
    onRemove: () => {
      setYamlFile(null)
    },
  }

  const handleUpload = async (values: any) => {
    if (!yamlFile) {
      message.error('Please select a YAML file')
      return
    }

    setUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', yamlFile)
      formData.append('name', values.name)
      formData.append('description', values.description || '')
      formData.append('domain', values.domain || '')

      const response = await axios.post('http://localhost:5000/api/schema/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      setGeneratedDatasetId(response.data.id)
      message.success('Schema uploaded and training data generated!')
      setStep(1)
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Failed to upload schema')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <Title level={2}>
          <FileOutlined /> Upload Training Schema
        </Title>
        <Text type="secondary">
          Upload a YAML schema file to automatically generate training data
        </Text>
      </div>

      <Steps current={step} style={{ marginBottom: '32px' }}>
        <Steps.Step title="Upload Schema" />
        <Steps.Step title="Success" />
      </Steps>

      {step === 0 && (
        <Card>
          <Form form={form} layout="vertical" onFinish={handleUpload}>
            {/* Schema File Upload */}
            <Form.Item label="Schema File (YAML)" required>
              <Dragger {...uploadProps}>
                <p className="ant-upload-drag-icon">
                  <CloudUploadOutlined style={{ fontSize: '48px' }} />
                </p>
                <p className="ant-upload-text">
                  Click or drag YAML file to upload
                </p>
                <p className="ant-upload-hint">
                  Schema file in limited_schemas.yaml format. Max 10MB.
                </p>
              </Dragger>
            </Form.Item>

            {/* Dataset Name */}
            <Form.Item
              label="Dataset Name"
              name="name"
              rules={[{ required: true, message: 'Please enter dataset name' }]}
            >
              <Input placeholder="e.g., My Custom Schema Training Data" />
            </Form.Item>

            {/* Description */}
            <Form.Item label="Description" name="description">
              <TextArea 
                rows={4} 
                placeholder="Describe this training dataset..."
              />
            </Form.Item>

            {/* Domain */}
            <Form.Item label="Domain" name="domain">
              <Select placeholder="Select domain (optional)">
                <Option value="Maximo">Maximo</Option>
                <Option value="SQL">Generic SQL</Option>
                <Option value="Custom">Custom</Option>
              </Select>
            </Form.Item>

            {/* Example Schema Format */}
            <Card size="small" style={{ background: '#f5f5f5', marginBottom: '24px' }}>
              <Text strong>Expected YAML Format:</Text>
              <pre style={{ fontSize: '12px', marginTop: '8px' }}>
{`workorder:
  description: The WORKORDER Table
  columns:
  - wonum:
      type: varchar(25)
      description: Work order number
      sample_value: "W001"
  - status:
      type: varchar(16)
      description: Work order status
      allowed_values:
      - value: APPR
        description: Approved
      - value: COMP
        description: Completed`}
              </pre>
            </Card>

            {/* Submit Button */}
            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={uploading}
                  icon={<CloudUploadOutlined />}
                >
                  Upload & Generate Training Data
                </Button>
                <Button onClick={() => navigate('/training/datasets/list')}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      )}

      {step === 1 && (
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <CheckCircleOutlined 
            style={{ fontSize: '64px', color: '#52c41a', marginBottom: '24px' }} 
          />
          <Title level={3}>Schema Uploaded Successfully!</Title>
          <Text>
            Your schema has been processed and training data has been generated.
          </Text>

          <Space style={{ marginTop: '24px' }}>
            <Button 
              type="primary" 
              onClick={() => navigate(`/training/datasets/${generatedDatasetId}/search`)}
            >
              View Dataset
            </Button>
            <Button onClick={() => navigate('/training/datasets/list')}>
              Go to Datasets
            </Button>
            <Button onClick={() => {
              setStep(0)
              form.resetFields()
              setYamlFile(null)
            }}>
              Upload Another
            </Button>
          </Space>
        </Card>
      )}
    </div>
  )
}

export default SchemaUploadPage
